<template>
  <div class="hb-games">
    <div v-if="!embedded" class="hb-table-title">{{ $t('trainingHistory') }} <span v-if="total" class="hb-table-count">({{ total }})</span></div>

    <!-- LOADING -->
    <div v-if="loading" class="hb-table-empty">{{ $t('loading') }}</div>

    <!-- EMPTY -->
    <div v-else-if="!rows.length" class="hb-table-empty">
      {{ $t('noTrainingsYet') }}
    </div>

    <!-- TABLE -->
    <div v-else>
      <div class="hb-table-wrap"
        :class="{ 'fade-left': canScrollLeft, 'fade-right': canScrollRight }">
        <div ref="tableScrollRef" class="hb-table-scroll" @scroll="updateScrollFade">
          <table class="hb-table">
            <thead>
              <tr>
                <th class="th-date">{{ $t('date') }}</th>
                <th class="th-org">{{ $t('organizationColumn') }}</th>
                <th class="th-type">{{ $t('eventType') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r._id" class="tr-clickable" @click="router.push(`/event/${r._id}`)">
                <td class="td-date">{{ formatDate(r.date) }}</td>
                <td class="td-org">
                  <div class="tr-org-cell">
                    <img v-if="orgLogoUrl(r)" :src="orgLogoUrl(r)!" alt="" class="tr-org-logo" />
                    <div v-else class="tr-org-placeholder">{{ getInitial(r.orgName) }}</div>
                    <span class="tr-org-name">{{ r.orgName || '-' }}</span>
                  </div>
                </td>
                <td class="td-type">
                  <span class="tr-type-badge" :class="`tr-type--${r.type}`">{{ typeLabel(r.type) }}</span>
                  <span class="tr-event-title">{{ r.eventTitle }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!embedded && rows.length < total" style="padding: 0 4px; margin-top: 8px;">
        <button class="load-more-btn" :disabled="loadingMore" @click="loadMore">
          <v-progress-circular v-if="loadingMore" indeterminate size="16" width="2" />
          <template v-else>{{ $t('loadMore') }} <span class="load-more-count">({{ total - rows.length }})</span></template>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axiosInstance from '@/services/axiosInstance'
import { getTeamImage, getLeagueImage } from '@/utils/imageUtils'
import { useDisplay } from 'vuetify'

const router = useRouter()

const { smAndDown } = useDisplay()

const props = defineProps<{
  userId: string
  /** When true, hide internal title & load-more (for external ios-section layout) */
  embedded?: boolean
}>()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

type Row = {
  _id: string
  type: string
  date: string | null
  orgName: string | null
  orgLogo: string | null
  orgType: 'league' | 'team'
  eventTitle: string | null
}

const loading = ref(false)
const loadingMore = ref(false)
const rows = ref<Row[]>([])
const total = ref(0)
const limit = ref(5)
const offset = ref(0)

const tableScrollRef = ref<HTMLElement | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

const updateScrollFade = () => {
  const el = tableScrollRef.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 2
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
}

const getInitial = (name?: string | null) => (name ? name.charAt(0).toUpperCase() : '?')

const orgLogoUrl = (row: Row) => {
  if (!row.orgLogo) return null
  if (row.orgType === 'league') return getLeagueImage(row.orgLogo)
  return getTeamImage(row.orgLogo)
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  if (smAndDown.value) {
    return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: '2-digit' })
  }
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: '2-digit' })
}

const { t } = useI18n({ useScope: 'global' })

const typeLabel = (type: string) => {
  switch (type) {
    case 'training': return t('typeTraining')
    case 'blue-white': return t('typeBlueWhite')
    case 'friendly-match': return t('typeFriendlyMatch')
    default: return type
  }
}

const fetchHistory = async (append = false) => {
  if (!props.userId) return
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  try {
    const { data } = await axiosInstance.get(
      `${API_BASE_URL}/stats/user/${props.userId}/training-history`,
      { params: { limit: limit.value, offset: offset.value } }
    )
    const items: Row[] = Array.isArray(data?.items) ? data.items : []
    total.value = data?.total || 0

    if (append) {
      rows.value = [...rows.value, ...items]
    } else {
      rows.value = items
    }
  } catch (e) {
    console.error('❌ Failed to load training history:', e)
    if (!append) rows.value = []
  } finally {
    loading.value = false
    loadingMore.value = false
    nextTick(updateScrollFade)
  }
}

const loadMore = () => {
  offset.value += limit.value
  limit.value = 10
  fetchHistory(true)
}

onMounted(() => {
  fetchHistory(false)
  nextTick(updateScrollFade)
  window.addEventListener('resize', updateScrollFade)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScrollFade)
})

watch(() => props.userId, () => {
  offset.value = 0
  fetchHistory(false)
})

defineExpose({ total, rows, loadMore, loadingMore })
</script>

<style scoped>
/* match sticky-column bg to page background */
.hb-table-wrap {
  --v-theme-sheetBackground: var(--v-theme-background);
}

.hb-games {
  padding: 0 4px;
}

.th-date, .td-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  white-space: nowrap;
}

.th-org, .td-org {
  min-width: 120px;
}

.th-type, .td-type {
  min-width: 140px;
}

@media (min-width: 600px) {
  .th-date, .td-date {
    width: 140px;
    min-width: 140px;
    max-width: 140px;
  }
}

/* Organization cell */
.tr-org-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tr-org-logo {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.tr-org-placeholder {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 800;
  background: rgba(var(--v-theme-ice), 0.12);
  color: rgb(var(--v-theme-ice));
  flex-shrink: 0;
}

.tr-org-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

/* Type badge */
.td-type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tr-type-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 7px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.tr-type--training {
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
}

.tr-type--blue-white {
  background: rgba(33, 150, 243, 0.15);
  color: #2196f3;
}

.tr-type--friendly-match {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.tr-event-title {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

.tr-clickable {
  cursor: pointer;
  transition: background 0.15s;
}
.tr-clickable:active {
  background: rgba(var(--v-theme-ice), 0.08);
}
</style>
